/* custom-cart-drawer.js — portable slide-out cart. No theme dependencies: builds its own DOM, talks to the
   Cart AJAX API, and mirrors Dawn's cart-count bubble when it finds one. Config comes from #custom-cart-drawer-config. */
(() => {
  const cfgEl = document.getElementById('custom-cart-drawer-config');
  if (!cfgEl || customElements.get('custom-cart-drawer')) return;
  const cfg = JSON.parse(cfgEl.textContent);
  const dec = (v) => { const t = document.createElement('textarea'); t.innerHTML = v; return t.value; };   // Liquid's `t` filter HTML-escapes
  const S = Object.fromEntries(Object.entries(cfg.strings).map(([k, v]) => [k, dec(v)]));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {
    const f = cfg.money_format || '{{amount}}';
    const num = (n, dec, sep, dsep) => { const s = (n / 100).toFixed(dec); const [i, d] = s.split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dsep + d : ''); };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({ amount: num(cents, 2, ',', '.'), amount_no_decimals: num(cents, 0, ',', '.'), amount_with_comma_separator: num(cents, 2, '.', ','), amount_no_decimals_with_comma_separator: num(cents, 0, '.', ','), amount_with_apostrophe_separator: num(cents, 2, "'", '.'), amount_no_decimals_with_space_separator: num(cents, 0, ' ', '.'), amount_with_space_separator: num(cents, 2, ' ', ',') }[k] ?? num(cents, 2, ',', '.'))).replace(/<[^>]+>/g, '');
  };
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class CustomCartDrawer extends HTMLElement {
    connectedCallback() {
      this.innerHTML = `
        <div class="ccd__overlay" data-ccd-close></div>
        <aside class="ccd__panel" role="dialog" aria-modal="true" aria-labelledby="ccd-title" tabindex="-1">
          <header class="ccd__head">
            <h2 class="ccd__title" id="ccd-title">${esc(S.title)} <span class="ccd__count" data-ccd-count></span></h2>
            <button type="button" class="ccd__close" data-ccd-close aria-label="${esc(S.close)}"><svg viewBox="0 0 18 17" width="18" height="17" aria-hidden="true"><path d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z" fill="currentColor"/></svg></button>
          </header>
          <div class="ccd__shipping" data-ccd-shipping hidden><p data-ccd-shipping-text></p><div class="ccd__bar" aria-hidden="true"><span data-ccd-shipping-bar></span></div></div>
          <p class="ccd__status" data-ccd-status role="status" aria-live="polite"></p>
          <div class="ccd__body" data-ccd-items></div>
          <footer class="ccd__foot" data-ccd-foot hidden>
            ${cfg.show_note ? `<label class="ccd__note"><span>${esc(S.note)}</span><textarea name="note" rows="2" data-ccd-note></textarea></label>` : ''}
            <div class="ccd__subtotal"><span>${esc(S.subtotal)}</span><strong data-ccd-subtotal></strong></div>
            ${cfg.coupon ? `<div class="ccd__coupon" data-ccd-coupon>
              <form class="ccd__coupon-form" data-ccd-coupon-form novalidate>
                <label class="ccd__coupon-label" for="ccd-coupon-input">${esc(S.coupon_label)}</label>
                <div class="ccd__coupon-row">
                  <input class="ccd__coupon-input" id="ccd-coupon-input" type="text" name="discount" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="${esc(S.coupon_placeholder)}" data-ccd-coupon-input>
                  <button type="submit" class="ccd__coupon-apply" data-ccd-coupon-apply>${esc(S.coupon_apply)}</button>
                </div>
              </form>
              <p class="ccd__coupon-msg" data-ccd-coupon-msg role="status" hidden></p>
              <ul class="ccd__coupon-list list-unstyled" data-ccd-coupon-list role="list"></ul>
            </div>` : ''}
            <p class="ccd__taxes">${esc(S.taxes)}</p>
            ${cfg.notice ? `<p class="ccd__notice">${esc(cfg.notice)}</p>` : ''}
            <a class="ccd__checkout" href="${esc(cfg.checkout_url)}" data-ccd-checkout>${esc(S.checkout)}</a>
            <button type="button" class="ccd__continue" data-ccd-close>${esc(S.continue)}</button>
          </footer>
        </aside>`;
      this.panel = this.querySelector('.ccd__panel');
      this.querySelectorAll('[data-ccd-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      document.addEventListener('keydown', (e) => { if (this.classList.contains('is-open')) this.onKey(e); });
      this.addEventListener('click', (e) => this.onClick(e));
      this.addEventListener('change', (e) => this.onChange(e));
      document.addEventListener('custom-cart:open', () => this.refresh().then(() => this.open()));
      document.addEventListener('custom-cart:updated', () => this.refresh());
      document.addEventListener('submit', (e) => this.onSubmit(e), true);          // capture: runs before theme handlers
      document.addEventListener('click', (e) => {
        const a = e.target.closest('a[href]'); if (!a) return;
        const href = a.getAttribute('href').split('?')[0];
        if (href === cfg.cart_url || href.endsWith('/cart') || a.id === 'cart-icon-bubble') { e.preventDefault(); this.refresh().then(() => this.open(a)); }
      }, true);
      this.refresh();
    }
    /* ---- data ---- */
    async refresh() {
      try { this.cart = await (await fetch(cfg.cart_js, { headers: { Accept: 'application/json' } })).json(); this.render(); }
      catch (e) { this.status(S.error); }
    }
    async change(key, quantity) {
      const r = await fetch(cfg.change_url + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      const json = await r.json();
      if (!r.ok || json.status) { this.status(json.description || json.message || S.error); return this.refresh(); }
      this.cart = json; this.render(); this.status('');
    }
    async add(form) {
      const fd = new FormData(form); fd.delete('sections'); fd.delete('sections_url');
      const r = await fetch(cfg.add_url + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      const json = await r.json();
      if (!r.ok || json.status) throw new Error(json.description || json.message || S.error);
      await this.refresh(); this.status(S.added); return json;
    }
    /* ---- render ---- */
    render() {
      const c = this.cart; if (!c) return;
      const ae = document.activeElement; const keep = ae && this.contains(ae) ? { key: ae.closest('.ccd__item')?.dataset.key, sel: ae.hasAttribute('data-ccd-plus') ? '[data-ccd-plus]' : ae.hasAttribute('data-ccd-minus') ? '[data-ccd-minus]' : ae.hasAttribute('data-ccd-qty') ? '[data-ccd-qty]' : null } : null;
      const items = this.querySelector('[data-ccd-items]'); const foot = this.querySelector('[data-ccd-foot]');
      this.querySelector('[data-ccd-count]').textContent = c.item_count ? `(${c.item_count})` : '';
      if (!c.items.length) { items.innerHTML = `<p class="ccd__empty">${esc(S.empty)}</p><button type="button" class="ccd__continue ccd__continue--empty" data-ccd-close>${esc(S.continue)}</button>`; foot.hidden = true; }
      else {
        items.innerHTML = `<ul class="ccd__list" role="list">${c.items.map((it) => `
          <li class="ccd__item" data-key="${esc(it.key)}">
            ${it.image ? `<a href="${esc(it.url)}" class="ccd__img"><img src="${esc(it.image.replace(/(\.[a-z]+)(\?|$)/i, '_240x240$1$2'))}" alt="${esc(it.featured_image?.alt || it.product_title)}" width="120" height="120" loading="lazy"></a>` : '<span class="ccd__img"></span>'}
            <div class="ccd__info">
              <a href="${esc(it.url)}" class="ccd__name">${esc(it.product_title)}</a>
              ${it.variant_title && it.variant_title !== 'Default Title' ? `<p class="ccd__variant">${esc(it.variant_title)}</p>` : ''}
              ${(it.properties && Object.keys(it.properties).length) ? `<p class="ccd__variant">${Object.entries(it.properties).filter(([k]) => !k.startsWith('_')).map(([k, v]) => esc(k) + ': ' + esc(v)).join(' · ')}</p>` : ''}
              <div class="ccd__row">
                <div class="ccd__qty" role="group" aria-label="${esc(S.quantity)}">
                  <button type="button" data-ccd-minus aria-label="${esc(S.decrease)} ${esc(it.product_title)}">−</button>
                  <input type="number" inputmode="numeric" min="0" value="${it.quantity}" data-ccd-qty aria-label="${esc(S.quantity)} ${esc(it.product_title)}">
                  <button type="button" data-ccd-plus aria-label="${esc(S.increase)} ${esc(it.product_title)}">+</button>
                </div>
                <div class="ccd__price">${it.original_line_price !== it.final_line_price ? `<s>${money(it.original_line_price)}</s> ` : ''}<strong>${money(it.final_line_price)}</strong></div>
              </div>
            </div>
            <button type="button" class="ccd__remove" data-ccd-remove aria-label="${esc(S.remove)} ${esc(it.product_title)}"><svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1ZM6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02ZM11.75 14h-7.5V4h7.5v10Z" fill="currentColor"/><path d="M6.55 12.5a.5.5 0 0 0 .5-.5V7a.5.5 0 0 0-1 0v5c0 .28.22.5.5.5ZM9.45 12.5a.5.5 0 0 0 .5-.5V7a.5.5 0 0 0-1 0v5c0 .28.22.5.5.5Z" fill="currentColor"/></svg></button>
          </li>`).join('')}</ul>`;
        foot.hidden = false;
        this.querySelector('[data-ccd-subtotal]').textContent = money(c.items_subtotal_price);
        if (cfg.coupon) this.renderCoupon(c);
        if (cfg.show_note) { const n = this.querySelector('[data-ccd-note]'); if (n && document.activeElement !== n) n.value = c.note || ''; }
      }
      const ship = this.querySelector('[data-ccd-shipping]');
      if (cfg.free_shipping_threshold > 0 && c.items.length) {
        /* Shopify tests the free-shipping rule on the total AFTER discounts, so a coupon can push a
           cart back under the threshold. `total_price` is that number (items_subtotal_price is the
           pre-discount figure and would keep promising free shipping the checkout will not give). */
        const qualifying = c.total_price;
        const left = cfg.free_shipping_threshold - qualifying; ship.hidden = false;
        this.querySelector('[data-ccd-shipping-text]').textContent = left > 0 ? S.free_shipping_left.replace('[amount]', money(left)) : S.free_shipping_done;
        this.querySelector('[data-ccd-shipping-bar]').style.width = Math.min(100, Math.round(qualifying / cfg.free_shipping_threshold * 100)) + '%';
      } else ship.hidden = true;
      this.updateBubbles(c.item_count);
      if (keep) { const el = keep.key && keep.sel ? this.querySelector(`.ccd__item[data-key="${keep.key}"] ${keep.sel}`) : null; (el || this.querySelector('.ccd__close')).focus({ preventScroll: true }); }
    }
    /* ---- coupon ----------------------------------------------------------------
       Shopify has no cart-level coupon field; a code belongs to the checkout. But
       `POST /cart/update.js {discount: CODE}` attaches it to the session and the cart
       recalculates, and the code then carries straight into the checkout.
       It REPLACES the whole list, so re-sending the valid ones is how a rejected code
       is rolled back. An unknown code still answers 200 — the signal is
       `discount_codes[].applicable === false`. */
    renderCoupon(c) {
      const list = this.querySelector('[data-ccd-coupon-list]');
      const msg = this.querySelector('[data-ccd-coupon-msg]');
      const form = this.querySelector('[data-ccd-coupon-form]');
      if (!list) return;
      const good = (c.discount_codes || []).filter((d) => d.applicable);
      list.innerHTML = good.map((d) => `
        <li class="ccd__coupon-item">
          <span class="ccd__coupon-code">${esc(d.code.toUpperCase())}</span>
          <span class="ccd__coupon-amount">−${money(c.total_discount)}</span>
          <button type="button" class="ccd__coupon-remove" data-ccd-coupon-remove="${esc(d.code)}" aria-label="${esc(S.coupon_remove)} ${esc(d.code)}">×</button>
        </li>`).join('');
      if (form) form.hidden = good.length > 0;
      if (msg) {
        if (this.couponError) { msg.hidden = false; msg.textContent = this.couponError; msg.classList.add('ccd__coupon-msg--error'); }
        else if (good.length) { msg.hidden = false; msg.textContent = S.coupon_applied; msg.classList.remove('ccd__coupon-msg--error'); }
        else { msg.hidden = true; msg.textContent = ''; }
      }
    }
    async applyCoupon(code) {
      const clean = String(code || '').trim();
      if (!clean) return;
      const previous = (this.cart?.discount_codes || []).filter((d) => d.applicable).map((d) => d.code);
      const btn = this.querySelector('[data-ccd-coupon-apply]');
      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      try {
        let cart = await this.setDiscount(clean);
        const ok = (cart.discount_codes || []).some((d) => d.code.toLowerCase() === clean.toLowerCase() && d.applicable);
        if (!ok) {
          this.couponError = S.coupon_invalid.replace('[code]', clean);
          cart = await this.setDiscount(previous.join(','));          // put the working ones back
        } else this.couponError = '';
        this.cart = cart;
      } catch (err) { this.couponError = S.error; }
      finally { if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); } }
      this.render();
      this.status(this.couponError || S.coupon_applied);
    }
    async removeCoupon() {
      this.couponError = '';
      try { this.cart = await this.setDiscount(''); } catch (err) { this.couponError = S.error; }
      this.render();
    }
    async setDiscount(value) {
      const r = await fetch(cfg.cart_url + '/update.js', { method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ discount: value }) });
      if (!r.ok) throw new Error('discount');
      return r.json();
    }
    updateBubbles(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count));
      const icon = document.getElementById('cart-icon-bubble');                                    // Dawn header
      if (icon) {
        let b = icon.querySelector('.cart-count-bubble');
        if (count > 0) {
          if (!b) { b = document.createElement('div'); b.className = 'cart-count-bubble'; icon.appendChild(b); }
          b.innerHTML = `<span aria-hidden="true">${count < 100 ? count : '99+'}</span><span class="visually-hidden">${esc(S.items_count.replace('[count]', count))}</span>`;
        } else if (b) b.remove();
      }
    }
    status(msg) { this.querySelector('[data-ccd-status]').textContent = msg || ''; }
    /* ---- events ---- */
    onClick(e) {
      const rm = e.target.closest('[data-ccd-coupon-remove]');
      if (rm) { e.preventDefault(); return this.removeCoupon(); }
      const item = e.target.closest('.ccd__item'); if (!item) return;
      const key = item.dataset.key; const input = item.querySelector('[data-ccd-qty]');
      if (e.target.closest('[data-ccd-remove]')) return this.change(key, 0);
      if (e.target.closest('[data-ccd-plus]')) return this.change(key, Number(input.value) + 1);
      if (e.target.closest('[data-ccd-minus]')) return this.change(key, Math.max(0, Number(input.value) - 1));
    }
    onChange(e) {
      if (e.target.matches('[data-ccd-qty]')) this.change(e.target.closest('.ccd__item').dataset.key, Math.max(0, Number(e.target.value) || 0));
      if (e.target.matches('[data-ccd-note]')) fetch(cfg.cart_url + '/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
    }
    async onSubmit(e) {
      const form = e.target; if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute('data-ccd-coupon-form')) {
        e.preventDefault(); e.stopPropagation();
        const input = form.querySelector('[data-ccd-coupon-input]');
        return this.applyCoupon(input && input.value);
      }
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      e.preventDefault(); e.stopPropagation();
      const btn = form.querySelector('[type="submit"]'); if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
      try { await this.add(form); document.querySelectorAll('quick-add-modal[open], .quick-add-modal[open]').forEach((m) => (typeof m.hide === 'function' ? m.hide() : m.removeAttribute('open')));   /* Dawn's size chooser */ this.open(btn || form); }
      catch (err) { this.refresh(); this.open(btn || form); this.status(err.message); }
      finally { if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); } }
    }
    onKey(e) {
      if (e.key === 'Escape') return this.close();
      if (e.key !== 'Tab') return;
      const els = [...this.panel.querySelectorAll(focusable)].filter((el) => el.offsetParent !== null); if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    open(opener) {
      this.opener = opener && opener.focus ? opener : document.activeElement;
      this.classList.add('is-open'); this.setAttribute('aria-hidden', 'false'); document.body.classList.add('ccd-open');
      setTimeout(() => (this.querySelector('.ccd__close') || this.panel).focus(), 50);
      document.dispatchEvent(new CustomEvent('custom-cart:opened'));
    }
    close() {
      this.classList.remove('is-open'); this.setAttribute('aria-hidden', 'true'); document.body.classList.remove('ccd-open');
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('custom-cart:closed'));
    }
  }
  customElements.define('custom-cart-drawer', CustomCartDrawer);
  const mount = () => { if (!document.querySelector('custom-cart-drawer')) { const el = document.createElement('custom-cart-drawer'); el.setAttribute('aria-hidden', 'true'); document.body.appendChild(el); } };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', mount) : mount();
})();


/* ---- the same coupon field on the cart page -------------------------------------
   Dawn's /cart renders its own totals server-side, so after a code is applied the page
   is reloaded and Dawn redraws the discount itself. Mounted from our own file; no Dawn
   template is touched. */
(() => {
  const cfgEl = document.getElementById('custom-cart-drawer-config');
  if (!cfgEl) return;
  const cfg = JSON.parse(cfgEl.textContent);
  if (!cfg.coupon) return;
  const dec = (v) => { const t = document.createElement('textarea'); t.innerHTML = v; return t.value; };
  const S = Object.fromEntries(Object.entries(cfg.strings).map(([k, v]) => [k, dec(v)]));
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (cents) => {                                   /* cart.js has no *_formatted field */
    const f = cfg.money_format || '{{amount}}';
    const num = (n, d, sep, dsep) => { const s = (n / 100).toFixed(d); const [i, dd] = s.split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (dd ? dsep + dd : ''); };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({ amount: num(cents, 2, ',', '.'), amount_no_decimals: num(cents, 0, ',', '.'), amount_with_comma_separator: num(cents, 2, '.', ','), amount_no_decimals_with_comma_separator: num(cents, 0, '.', ',') }[k] ?? num(cents, 2, ',', '.'))).replace(/<[^>]+>/g, '');
  };

  const setDiscount = async (value) => {
    const r = await fetch(cfg.cart_url + '/update.js', { method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: value }) });
    if (!r.ok) throw new Error('discount');
    return r.json();
  };

  const mount = async () => {
    const host = document.querySelector('.cart__footer .cart__blocks, .cart__footer, #main-cart-footer .cart__blocks');
    if (!host || host.querySelector('[data-ccdp-coupon]')) return;
    const cart = await (await fetch(cfg.cart_url + '.js')).json();
    if (!cart.items.length) return;
    const good = (cart.discount_codes || []).filter((d) => d.applicable);
    const box = document.createElement('div');
    box.className = 'ccd__coupon ccdp__coupon';
    box.setAttribute('data-ccdp-coupon', '');
    box.innerHTML = good.length
      ? `<p class="ccd__coupon-msg">${esc(S.coupon_applied)}</p>
         <ul class="ccd__coupon-list list-unstyled" role="list">${good.map((d) => `
           <li class="ccd__coupon-item"><span class="ccd__coupon-code">${esc(d.code.toUpperCase())}</span>
           <span class="ccd__coupon-amount">−${esc(money(cart.total_discount))}</span>
           <button type="button" class="ccd__coupon-remove" data-ccdp-remove aria-label="${esc(S.coupon_remove)} ${esc(d.code)}">×</button></li>`).join('')}</ul>`
      : `<form class="ccd__coupon-form" data-ccdp-form novalidate>
           <label class="ccd__coupon-label" for="ccdp-coupon">${esc(S.coupon_label)}</label>
           <div class="ccd__coupon-row">
             <input class="ccd__coupon-input" id="ccdp-coupon" type="text" autocomplete="off" autocapitalize="characters" spellcheck="false" placeholder="${esc(S.coupon_placeholder)}">
             <button type="submit" class="ccd__coupon-apply">${esc(S.coupon_apply)}</button>
           </div>
         </form>
         <p class="ccd__coupon-msg ccd__coupon-msg--error" data-ccdp-msg role="status" hidden></p>`;
    host.prepend(box);

    box.addEventListener('submit', async (e) => {
      if (!e.target.hasAttribute('data-ccdp-form')) return;
      e.preventDefault();
      const input = e.target.querySelector('input');
      const code = (input.value || '').trim();
      if (!code) return;
      const btn = e.target.querySelector('button[type="submit"]');
      btn.disabled = true;
      const msg = box.querySelector('[data-ccdp-msg]');
      try {
        const c = await setDiscount(code);
        const ok = (c.discount_codes || []).some((d) => d.code.toLowerCase() === code.toLowerCase() && d.applicable);
        if (ok) return window.location.reload();
        await setDiscount('');
        msg.hidden = false; msg.textContent = S.coupon_invalid.replace('[code]', code);
      } catch (err) { msg.hidden = false; msg.textContent = S.error; }
      finally { btn.disabled = false; }
    });
    box.addEventListener('click', async (e) => {
      if (!e.target.closest('[data-ccdp-remove]')) return;
      await setDiscount('');
      window.location.reload();
    });
  };
  const start = () => { if (/\/cart/.test(location.pathname)) mount(); };
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', start) : start();
  document.addEventListener('shopify:section:load', start);
})();
